import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTransactions } from './useTransactions';
import { useCards } from './useCards';
import { useGoals } from './useGoals';
import { useAccounts } from './useAccounts';
import { syncAccountBalance } from '../lib/accountBalance';

export type CalendarEventType =
  | 'manual_note'
  | 'bill_due'
  | 'card_due'
  | 'card_closing'
  | 'recurring_transaction'
  | 'installment'
  | 'goal_deadline'
  | 'payment_reminder'
  | 'income_expected'
  | 'income_received'
  | 'transfer';

export interface CalendarEvent {
  id: string;
  user_id?: string;
  title: string;
  description?: string;
  event_type: CalendarEventType;
  start_date: string;
  end_date?: string | null;
  all_day: boolean;
  source_module?: string | null;
  linked_transaction_id?: string | null;
  linked_credit_card_id?: string | null;
  linked_goal_id?: string | null;
  linked_account_id?: string | null;
  is_manual: boolean;
  color: string;
  status: string;
  amount?: number | null;
  // New v2 fields
  google_calendar_event_id?: string | null;
  recurrence_group_id?: string | null;
  source_transaction_id?: string | null;
  is_calendar_linked?: boolean;
  sync_with_google?: boolean;
  // Computed metadata for detail modal
  account_name?: string | null;
  card_name?: string | null;
  goal_name?: string | null;
  category?: string | null;
  notes?: string | null;
  recurrence?: string | null;
  created_at?: string;
  updated_at?: string;
  // Installment metadata (from transaction)
  installment_number?: number | null;
  installments?: number | null;
  /** Data real de pagamento/recebimento — diferente de start_date quando pago antecipado/atrasado */
  paid_at?: string | null;
}

export type CalendarEventPayload = Omit<
  CalendarEvent,
  'id' | 'user_id' | 'created_at' | 'updated_at' | 'account_name' | 'card_name' | 'goal_name'
>;

export const EVENT_TYPE_COLORS: Record<CalendarEventType, string> = {
  manual_note: '#6366f1',
  bill_due: '#ef4444',
  card_due: '#f97316',
  card_closing: '#f59e0b',
  recurring_transaction: '#8b5cf6',
  installment: '#ec4899',
  goal_deadline: '#10b981',
  payment_reminder: '#ef4444',
  income_expected: '#22c55e',
  income_received: '#16a34a',
  transfer: '#0d9488',
};

export const EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  manual_note: 'Anotação',
  bill_due: 'Vencimento',
  card_due: 'Fatura',
  card_closing: 'Fechamento',
  recurring_transaction: 'Recorrente',
  installment: 'Parcela',
  goal_deadline: 'Meta',
  payment_reminder: 'Lembrete',
  income_expected: 'Receita prevista',
  income_received: 'Receita recebida',
  transfer: 'Transferência',
};

export const EVENT_TYPE_ICONS: Record<CalendarEventType, string> = {
  manual_note: 'ri-edit-2-line',
  bill_due: 'ri-bill-line',
  card_due: 'ri-bank-card-line',
  card_closing: 'ri-scissors-cut-line',
  recurring_transaction: 'ri-repeat-line',
  installment: 'ri-stack-line',
  goal_deadline: 'ri-focus-3-line',
  payment_reminder: 'ri-alarm-line',
  income_expected: 'ri-arrow-up-circle-line',
  income_received: 'ri-check-double-line',
  transfer: 'ri-arrow-left-right-line',
};

const generateAutoEvents = (
  transactions: ReturnType<typeof useTransactions>['transactions'],
  cards: ReturnType<typeof useCards>['cards'],
  goals: ReturnType<typeof useGoals>['goals'],
  accounts: ReturnType<typeof useAccounts>['accounts'],
  year: number,
  month: number,
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  const pad = (n: number) => String(n).padStart(2, '0');
  const seenAutoIds = new Set<string>();

  transactions.forEach((t) => {
    // Respect user preference: if show_in_calendar is explicitly false, skip
    if (t.show_in_calendar === false) return;

    const d = new Date(t.date + 'T00:00:00');
    if (d.getFullYear() !== year || d.getMonth() + 1 !== month) return;

    const accountName = accounts.find((a) => a.id === t.account_id)?.name ?? null;
    const cardName = cards.find((c) => c.id === t.card_id)?.name ?? null;
    const isTransfer = (t as typeof t & { transaction_subtype?: string }).transaction_subtype === 'transferencia';

    // ── TRANSFER events: show only outgoing leg (despesa) to avoid duplication ─
    if (isTransfer) {
      if (t.type === 'despesa') {
        const evtId = `auto-transfer-${t.id}`;
        if (!seenAutoIds.has(evtId)) {
          seenAutoIds.add(evtId);
          events.push({
            id: evtId,
            title: t.description || 'Transferência',
            description: accountName ? `Conta: ${accountName}` : undefined,
            event_type: 'transfer',
            start_date: t.date,
            all_day: true,
            is_manual: false,
            color: EVENT_TYPE_COLORS.transfer,
            status: t.status,
            amount: t.amount,
            linked_transaction_id: t.id,
            linked_account_id: t.account_id ?? null,
            source_module: 'transactions',
            account_name: accountName,
            category: 'Transferência',
            notes: t.notes ?? null,
          });
        }
      }
      // Incoming leg (receita) is intentionally skipped to avoid duplication
      return;
    }

    if (t.type === 'despesa') {
      let evtType: CalendarEventType;
      let evtColor: string;

      // ── IMPORTANTE: checar recorrência ANTES de parcelamento ─────────────
      // Transações recorrentes têm recurrence != 'none' e installments=1
      // Parcelamentos têm recurrence=='none' e installments>1
      // A ordem garante que recorrências nunca sejam classificadas como parcelas
      if (t.recurrence && t.recurrence !== 'none') {
        evtType = 'recurring_transaction';
        evtColor = EVENT_TYPE_COLORS.recurring_transaction;
      } else if ((t.installments ?? 1) > 1) {
        evtType = 'installment';
        evtColor = EVENT_TYPE_COLORS.installment;
      } else {
        evtType = 'bill_due';
        evtColor = EVENT_TYPE_COLORS.bill_due;
      }

      const evtId = `auto-expense-${t.id}`;
      if (!seenAutoIds.has(evtId)) {
        seenAutoIds.add(evtId);
        events.push({
          id: evtId,
          title: t.description || 'Despesa',
          description: `${t.category ?? ''}${accountName ? ` • ${accountName}` : ''}${cardName ? ` • ${cardName}` : ''}`,
          event_type: evtType,
          start_date: t.date,
          all_day: true,
          is_manual: false,
          color: evtColor,
          status: t.status,
          amount: t.amount,
          linked_transaction_id: t.id,
          linked_account_id: t.account_id ?? null,
          linked_credit_card_id: t.card_id ?? null,
          source_module: 'transactions',
          account_name: accountName,
          card_name: cardName,
          category: t.category ?? null,
          notes: t.notes ?? null,
          recurrence: t.recurrence ?? null,
          // Para parcelamentos: mostra número da parcela
          installment_number: evtType === 'installment' ? (t.installment_number ?? null) : null,
          installments: evtType === 'installment' ? t.installments : null,
          // installment_group_id serve como recurrence_group_id para ambos os tipos
          recurrence_group_id: t.installment_group_id ?? null,
          paid_at: t.paid_at ?? null,
        });
      }
    }

    if (t.type === 'receita') {
      const isReceived = t.status === 'recebido' || t.status === 'pago';
      const evtType: CalendarEventType = isReceived ? 'income_received' : 'income_expected';
      const evtColor = isReceived ? EVENT_TYPE_COLORS.income_received : EVENT_TYPE_COLORS.income_expected;
      const evtId = `auto-income-${t.id}`;
      if (!seenAutoIds.has(evtId)) {
        seenAutoIds.add(evtId);
        events.push({
          id: evtId,
          title: t.description || 'Receita',
          description: `${t.category ?? ''}${accountName ? ` • ${accountName}` : ''}`,
          event_type: evtType,
          start_date: t.date,
          all_day: true,
          is_manual: false,
          color: evtColor,
          status: t.status,
          amount: t.amount,
          linked_transaction_id: t.id,
          linked_account_id: t.account_id ?? null,
          source_module: 'transactions',
          account_name: accountName,
          category: t.category ?? null,
          notes: t.notes ?? null,
          recurrence: t.recurrence ?? null,
          recurrence_group_id: t.installment_group_id ?? null,
          paid_at: t.paid_at ?? null,
        });
      }
    }
  });

  // Card closing and due events
  cards.forEach((card) => {
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    const closingDate = `${year}-${pad(month)}-${pad(Math.min(card.closing_day, lastDayOfMonth))}`;
    const dueDate = `${year}-${pad(month)}-${pad(Math.min(card.due_day, lastDayOfMonth))}`;
    const cardLabel = card.nickname || card.name;

    events.push({
      id: `auto-card-close-${card.id}-${year}-${month}`,
      title: `Fechamento — ${cardLabel}`,
      description: `Fatura do cartão ${card.bank_name || card.bank || cardLabel} fecha hoje`,
      event_type: 'card_closing',
      start_date: closingDate,
      all_day: true,
      is_manual: false,
      color: EVENT_TYPE_COLORS.card_closing,
      status: 'active',
      linked_credit_card_id: card.id,
      source_module: 'cards',
      card_name: cardLabel,
    });

    events.push({
      id: `auto-card-due-${card.id}-${year}-${month}`,
      title: `Vencimento — ${cardLabel}`,
      description: `Fatura do cartão ${card.bank_name || card.bank || cardLabel} vence hoje`,
      event_type: 'card_due',
      start_date: dueDate,
      all_day: true,
      is_manual: false,
      color: EVENT_TYPE_COLORS.card_due,
      status: 'active',
      linked_credit_card_id: card.id,
      source_module: 'cards',
      card_name: cardLabel,
    });
  });

  // Goals deadline
  goals.forEach((goal) => {
    if (!goal.target_date) return;
    const d = new Date(goal.target_date + 'T00:00:00');
    if (d.getFullYear() !== year || d.getMonth() + 1 !== month) return;
    events.push({
      id: `auto-goal-${goal.id}`,
      title: `Meta: ${goal.name}`,
      description: `Prazo da meta "${goal.name}" • ${goal.type}`,
      event_type: 'goal_deadline',
      start_date: goal.target_date,
      all_day: true,
      is_manual: false,
      color: EVENT_TYPE_COLORS.goal_deadline,
      status: 'active',
      linked_goal_id: goal.id,
      source_module: 'goals',
      goal_name: goal.name,
    });
  });

  return events;
};

export const useCalendarEvents = (year: number, month: number) => {
  const { user } = useAuth();
  const [manualEvents, setManualEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Security: clear stale manual events immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setManualEvents([]);
        setError(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const { transactions, refetch: refetchTransactions } = useTransactions({ year, month });
  const { cards } = useCards();
  const { goals } = useGoals();
  const { accounts } = useAccounts();

  const fetchManualEvents = useCallback(async () => {
    if (!user) {
      setManualEvents([]);   // Clear stale data on logout
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const pad = (n: number) => String(n).padStart(2, '0');
    const startDate = `${year}-${pad(month)}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${pad(month)}-${pad(lastDay)}`;

    const { data, error: err } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', user.id)        // Explicit user filter (defense in depth)
      .eq('is_manual', true)
      .gte('start_date', startDate)
      .lte('start_date', endDate)
      .order('start_date', { ascending: true });

    if (err) {
      console.error('[useCalendarEvents] fetch error:', err);
      setError('Não foi possível carregar os eventos. Tente novamente.');
    } else {
      // Double-check: only store events belonging to this user
      const safeData = ((data as CalendarEvent[]) ?? []).filter(
        (e) => e.user_id === user.id
      );
      setManualEvents(safeData);
    }
    setLoading(false);
  }, [user, year, month]);

  useEffect(() => { fetchManualEvents(); }, [fetchManualEvents]);

  const autoEvents = generateAutoEvents(transactions, cards, goals, accounts, year, month);

  // Merge auto + manual events, deduplicate by ID, sort by date
  const seenIds = new Set<string>();
  const allEvents = [...autoEvents, ...manualEvents]
    .filter((evt) => {
      if (seenIds.has(evt.id)) return false;
      seenIds.add(evt.id);
      return true;
    })
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  const createEvent = async (
    payload: CalendarEventPayload,
  ): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Usuário não autenticado.' };
    const { error: err } = await supabase
      .from('calendar_events')
      .insert({ ...payload, user_id: user.id });
    if (err) {
      console.error('[createCalendarEvent] error:', err);
      return { success: false, message: `Erro ao criar evento: ${err.message}` };
    }
    await fetchManualEvents();
    return { success: true, message: 'Evento criado com sucesso!' };
  };

  const updateEvent = async (
    id: string,
    payload: Partial<CalendarEventPayload>,
  ): Promise<{ success: boolean; message: string }> => {
    const { error: err } = await supabase
      .from('calendar_events')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (err) return { success: false, message: `Erro ao atualizar evento: ${err.message}` };
    await fetchManualEvents();
    return { success: true, message: 'Evento atualizado com sucesso!' };
  };

  const deleteEvent = async (
    id: string,
    mode: 'single' | 'future' | 'all' = 'single',
    recurrenceGroupId?: string | null,
    startDate?: string,
  ): Promise<{ success: boolean; message: string }> => {
    // ── Eventos auto-gerados (de transações) ──────────────────────────────────
    if (id.startsWith('auto-')) {
      const txId = id.replace(/^auto-(?:expense|income)-/, '');

      if (mode === 'all' && recurrenceGroupId) {
        // ── Soft-delete toda a série via group_id ─────────────────────────────
        const { data: sampleTx } = await supabase
          .from('transactions')
          .select('account_id')
          .eq('installment_group_id', recurrenceGroupId)
          .eq('user_id', user?.id ?? '')
          .maybeSingle();

        const { error: err } = await supabase
          .from('transactions')
          .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
          .eq('installment_group_id', recurrenceGroupId)
          .eq('user_id', user?.id ?? '');
        if (err) return { success: false, message: 'Erro ao excluir série de lançamentos.' };
        await syncAccountBalance(sampleTx?.account_id ?? null);

      } else if (mode === 'future' && recurrenceGroupId && startDate) {
        // ── Soft-delete esta e as próximas PENDENTES via group_id ─────────────
        const { data: sampleTx } = await supabase
          .from('transactions')
          .select('account_id')
          .eq('installment_group_id', recurrenceGroupId)
          .eq('user_id', user?.id ?? '')
          .maybeSingle();

        const { error: err } = await supabase
          .from('transactions')
          .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
          .eq('installment_group_id', recurrenceGroupId)
          .gte('date', startDate)
          .in('status', ['pendente', 'vencido', 'atrasado'])
          .eq('user_id', user?.id ?? '');
        if (err) return { success: false, message: 'Erro ao excluir ocorrências futuras.' };
        await syncAccountBalance(sampleTx?.account_id ?? null);

      } else if ((mode === 'all' || mode === 'future') && !recurrenceGroupId) {
        // ── Excluir recorrências SEM group_id ─────────────────────────────────
        const { data: sourceTx } = await supabase
          .from('transactions')
          .select('id, account_id, description, recurrence, type')
          .eq('id', txId)
          .eq('user_id', user?.id ?? '')
          .maybeSingle();

        if (sourceTx?.recurrence && sourceTx.recurrence !== 'none' && sourceTx.description) {
          const userId = user?.id ?? '';
          const desc  = sourceTx.description;
          const rec   = sourceTx.recurrence;
          const accId = sourceTx.account_id;
          const txType = sourceTx.type; // 'despesa' ou 'receita'

          // Buscar todos relacionados filtrando também pelo tipo (despesa/receita)
          const { data: allRelated } = await supabase
            .from('transactions')
            .select('id, account_id, status, date')
            .eq('user_id', userId)
            .eq('description', desc)
            .eq('recurrence', rec)
            .eq('type', txType);

          if (allRelated && allRelated.length > 0) {
            // Status que indicam "já liquidado" — excluir da seleção em modo 'future'
            const SETTLED_STATUSES = ['pago', 'recebido', 'done'];

            const filtered = allRelated.filter((t) => {
              const sameAccount = accId ? t.account_id === accId : true;
              if (!sameAccount) return false;

              if (mode === 'future' && startDate) {
                return t.date >= startDate && !SETTLED_STATUSES.includes(t.status ?? '');
              }
              return true; // mode === 'all': deletar toda a série
            });

            const idsToDelete = filtered.map((t) => t.id);

            if (idsToDelete.length > 0) {
              const { error: err } = await supabase
                .from('transactions')
                .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
                .in('id', idsToDelete)
                .eq('user_id', userId);

              if (err) return { success: false, message: 'Erro ao excluir lançamentos recorrentes.' };
              await syncAccountBalance(accId ?? null);
            }
          }
        } else {
          // Fallback: soft-delete só esta ocorrência
          const { data: existing } = await supabase
            .from('transactions')
            .select('account_id')
            .eq('id', txId)
            .maybeSingle();

          const { error: err } = await supabase
            .from('transactions')
            .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
            .eq('id', txId)
            .eq('user_id', user?.id ?? '');
          if (err) return { success: false, message: 'Erro ao excluir lançamento.' };
          await syncAccountBalance(existing?.account_id ?? null);
        }

      } else {
        // ── Soft-delete apenas esta ocorrência ───────────────────────────────
        const { data: existing } = await supabase
          .from('transactions')
          .select('account_id')
          .eq('id', txId)
          .maybeSingle();

        const { error: err } = await supabase
          .from('transactions')
          .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
          .eq('id', txId)
          .eq('user_id', user?.id ?? '');
        if (err) return { success: false, message: 'Erro ao excluir lançamento.' };
        await syncAccountBalance(existing?.account_id ?? null);
      }

      await refetchTransactions();
      return { success: true, message: 'Lançamento(s) excluído(s) com sucesso.' };
    }

    // ── Eventos manuais em calendar_events ────────────────────────────────────
    if (mode === 'all' && recurrenceGroupId) {
      const { error: err } = await supabase
        .from('calendar_events')
        .delete()
        .eq('recurrence_group_id', recurrenceGroupId)
        .eq('user_id', user?.id ?? '');
      if (err) return { success: false, message: 'Erro ao excluir série.' };
    } else if (mode === 'future' && recurrenceGroupId && startDate) {
      const { error: err } = await supabase
        .from('calendar_events')
        .delete()
        .eq('recurrence_group_id', recurrenceGroupId)
        .gte('start_date', startDate)
        .eq('user_id', user?.id ?? '');
      if (err) return { success: false, message: 'Erro ao excluir eventos futuros.' };
    } else {
      const { error: err } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id ?? '');
      if (err) return { success: false, message: 'Erro ao excluir evento.' };
    }
    await fetchManualEvents();
    return { success: true, message: 'Evento(s) excluído(s).' };
  };

  const eventsByDate = allEvents.reduce<Record<string, CalendarEvent[]>>((acc, evt) => {
    const key = evt.start_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(evt);
    return acc;
  }, {});

  return {
    allEvents,
    manualEvents,
    autoEvents,
    eventsByDate,
    loading,
    error,
    createEvent,
    updateEvent,
    deleteEvent,
    refetch: fetchManualEvents,
    refetchTransactions,
  };
};
